/**
 * Axios Configuration
 *
 * Sets up the axios default base URL for API calls.
 * In development: Uses the React proxy (relative URLs work)
 * In production: Uses REACT_APP_API_URL environment variable
 *
 * Also configures AES-256-GCM encryption for production payloads.
 */
import axios from 'axios';
import { isEncryptionEnabled, encryptPayload, decryptPayload } from '../utils/crypto';

// Set the base URL for all axios requests
// In development, this is empty (uses proxy from package.json)
// In production, this should be set to your backend URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

axios.defaults.baseURL = API_BASE_URL;

/** Use this for fetch() so API calls go to the backend (Railway) in production */
export { API_BASE_URL };

// Public API paths that should never trigger a login redirect on 401
const PUBLIC_API_PATTERNS = [
  '/api/assessments/share/',
  '/api/ai-interviews/session/',
  '/api/screenings/apply/',
  '/api/screenings/share/',
  '/api/screenings/public/candidate-review/',
];

const isPublicApiCall = (url) =>
  PUBLIC_API_PATTERNS.some((pattern) => url?.includes(pattern));

const hasExternalCreateHeader = (config) => {
  const h = config?.headers || {};
  return Boolean(
    h['x-external-create-code'] ||
      h['X-External-Create-Code'] ||
      h['x-external-interview-create-code'] ||
      h['X-External-Interview-Create-Code']
  );
};

// ── AES-256-GCM request encryption (production only) ─────────────────
axios.interceptors.request.use(async (config) => {
  if (!isEncryptionEnabled()) return config;

  config.headers = config.headers || {};

  // File uploads are sent as FormData and are never encrypted (the body is a
  // binary multipart stream). Asking for an encrypted *response* on those adds
  // nothing but forces a CORS preflight — which some in-app/mobile browsers
  // fail outright, killing the upload before it reaches the server. Leave
  // multipart requests completely untouched.
  if (config.data instanceof FormData) {
    return config;
  }

  // Tell the backend we want encrypted responses (GET, POST, etc.)
  config.headers['x-encrypted'] = 'true';

  // Encrypt request body when present (POST/PUT/PATCH with JSON)
  if (config.data && typeof config.data === 'object') {
    try {
      const encrypted = await encryptPayload(config.data);
      config.data = encrypted;
    } catch (err) {
      console.error('[crypto] Failed to encrypt request:', err);
    }
  }

  return config;
});

// ── Response interceptor: decrypt + error handling ───────────────────
axios.interceptors.response.use(
  async (response) => {
    // Decrypt encrypted responses (detect by payload shape, no header needed)
    if (
      isEncryptionEnabled() &&
      response.data &&
      typeof response.data === 'object' &&
      response.data.iv &&
      response.data.data &&
      response.data.tag &&
      Object.keys(response.data).length === 3
    ) {
      try {
        response.data = await decryptPayload(response.data);
      } catch (err) {
        console.error('[crypto] Failed to decrypt response:', err);
      }
    }
    return response;
  },
  async (error) => {
    // In production, the backend may encrypt *error* payloads too.
    // Decrypt them here so UI code can consistently read `err.response.data.message`.
    if (
      isEncryptionEnabled() &&
      error?.response?.data &&
      typeof error.response.data === 'object' &&
      error.response.data.iv &&
      error.response.data.data &&
      error.response.data.tag &&
      Object.keys(error.response.data).length === 3
    ) {
      try {
        error.response.data = await decryptPayload(error.response.data);
      } catch (err) {
        console.error('[crypto] Failed to decrypt error response:', err);
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
      });
    }

    if (
      error.response?.status === 401 &&
      !isPublicApiCall(error.config?.url) &&
      !hasExternalCreateHeader(error.config)
    ) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

export default axios;

